/**
 * Part pricing hooks (#3034, T13b1): category margin rules, per-part pricing mode,
 * customer prices, campaigns, category recalculation (preview, then apply) and the
 * canonical resolve endpoint, all on the staff-api `part-pricing` routes.
 *
 * Contract notes
 *  - The server is the only evaluator. Nothing here, or in the screens built on it, computes an
 *    effective price; a price shown for a part comes from `POST /inventory/part-prices/resolve`.
 *  - Management amounts are decimal TEXT in cents at four decimals (`"7050.0000"`); resolve and
 *    line prices are JSON numbers. Neither is turned into a float for arithmetic here.
 *  - Query keys carry the tenant, the user and the session family, so a cached answer of another principal, or of an earlier
 *    login of the same user, is never shown to this one (the same cache serves a tab through a sign-out / sign-in).
 *  - Every request is bound to the owner (tenant, user, session family, generation) and the permissions it began under. They are
 *    re-read from the auth store just before it is sent and again when the answer arrives: an answer that outlived its owner
 *    is never delivered to the screen, the cache or a notification (`LateResultError`).
 *  - Cost-bearing recalculation (preview, apply) needs `inventory.pricing.manage` AND `reports.view_financial`. Its hooks refuse
 *    before any request when either is missing NOW, so a denied user never calls an endpoint that carries catalog cost.
 *  - Idempotent endpoints (the three creates and recalculation apply) take a caller-owned
 *    `Idempotency-Key`. `useKeyedSubmit` keeps one request (payload AND key, kind and target) across an uncertain outcome, also
 *    across an unmount or reload (a bounded, validated journal in `sessionStorage` under the owner); only a determinate refusal or
 *    an explicit, confirmed discard lets a later submit mint a new key. Nothing is ever sent on mount.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  LateResultError,
  MANAGE_PERMISSIONS,
  OwnerChangedError,
  PRICING_READ_PERMISSION,
  PricingAccessError,
  RECALCULATE_PERMISSIONS,
  fenceHolds,
  isLocalRefusal,
  ownerId,
  scopeOfOwner,
  takeFence,
  usePricingAccess,
  type PricingAccess,
  type PricingFence,
  type PricingOutcome,
  type PricingOwner,
  type PricingScope,
} from '@/features/inventory/pricing/pricingSession';
import {
  beginFlight,
  dropCorruptJournal,
  endFlight,
  getJournalView,
  putEntry,
  recordAttempt,
  removeEntry,
  slotOf,
  useJournalView,
  type JournalEntry,
  type JournalKind,
  type JournalStatus,
} from '@/features/inventory/pricing/pricingJournal';
import { queryKeys } from './queryKeys';

// ---------------------------------------------------------------------------
// Types (the wire shapes of services/staff-api routes/part-pricing.ts)
// ---------------------------------------------------------------------------

export type PartPriceMode = 'manual' | 'category_margin';
export type RulePriceType = 'fixed_price' | 'discount_percent';
export type PricingPartyKind = 'company' | 'person';
export type PricingTargetKind = 'part' | 'category';
export type PartPriceSource = 'catalog' | 'category_margin' | 'customer_price' | 'campaign' | 'manual';

export interface OffsetPage<T> {
  data: T[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

export interface CategoryMarginRule {
  id: string;
  categoryId: string;
  categoryName: string;
  /** Margin on the selling price, percent at four decimals (`"20.0000"`). */
  marginPercent: string;
  isActive: boolean;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PricingWarning {
  code: 'special_price_above_catalog';
  target: PricingTargetKind;
  partId?: string;
  categoryId?: string;
  specialNetCents: string;
  catalogNetCents?: string;
  affectedParts?: number;
}

export interface CustomerPriceRule {
  id: string;
  companyId: string | null;
  personId: string | null;
  partId: string | null;
  categoryId: string | null;
  priceType: RulePriceType;
  /** Net cents at four decimals for a fixed price, percent for a discount. */
  value: string;
  validFrom: string | null;
  validTo: string | null;
  isActive: boolean;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Computed by the server from the tenant-local date. */
  activeToday: boolean;
}
export type CustomerPriceSaved = CustomerPriceRule & { warnings: PricingWarning[] };

export interface Campaign {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  activeToday: boolean;
  /** Present on list rows only. */
  itemCount?: number;
}

export interface CampaignItem {
  id: string;
  campaignId: string;
  partId: string | null;
  categoryId: string | null;
  priceType: RulePriceType;
  value: string;
  createdAt: string;
}
export type CampaignItemSaved = CampaignItem & { warnings: PricingWarning[] };

export interface CampaignDetailPage {
  campaign: Campaign;
  items: OffsetPage<CampaignItem>;
}

export interface PartPricingModeResult {
  id: string;
  priceMode: PartPriceMode;
  marginOverridePercent: string | null;
  sellPriceNetCents: string;
  updatedAt: string;
}

/**
 * What `GET /part-pricing/parts/:partId` answers: the shape of the PATCH result, read from the CURRENT row. A stored mode this
 * API does not know (a drifted legacy schema) is the explicit `unknown`, never `manual`: a screen shows it as needing attention
 * and offers no silent default.
 */
export interface CurrentPartPricing extends Omit<PartPricingModeResult, 'priceMode'> {
  priceMode: PartPriceMode | 'unknown';
}

export interface PriceCandidate {
  source: 'customer_price' | 'campaign';
  ruleId: string;
  campaignId: string | null;
  priceType: RulePriceType;
  value: number;
  unitNetCents: number;
  target: { kind: PricingTargetKind; id: string };
  categoryDistance: number | null;
}

export type ResolvedPartPrice =
  | {
      partId: string;
      status: 'resolved';
      unitNetCents: number;
      source: Exclude<PartPriceSource, 'manual'>;
      baseNetCents: number;
      priceMode: PartPriceMode;
      selected: PriceCandidate | null;
      candidates: PriceCandidate[];
      warnings: { code: 'special_price_above_catalog'; ruleId: string; unitNetCents: number; baseNetCents: number }[];
      fingerprint: string;
    }
  | { partId: string; status: 'unavailable'; reason: string }
  | { partId: string; status: 'part_not_found' };

export type ResolveContext = { bookingId: string } | { party: { kind: PricingPartyKind; id: string } };

export interface ResolvePartPricesBody {
  /** 1..500 part ids. */
  partIds: string[];
  /** Either a booking or an explicit party; the server derives the date from the tenant zone. */
  context?: ResolveContext;
}

export interface ResolvePartPricesResponse {
  /** Tenant-local date the server priced for. */
  asOf: string;
  party: { kind: PricingPartyKind; id: string } | null;
  partySource: string | null;
  fingerprint: string;
  prices: ResolvedPartPrice[];
}

export type RecalculationOutcome = 'change' | 'unchanged' | 'no_rule' | 'invalid_data' | 'excluded';

export interface RecalculationRow {
  partId: string;
  sku: string | null;
  name: string | null;
  categoryId: string | null;
  priceMode: string | null;
  outcome: RecalculationOutcome;
  /** `no_margin_rule`, an `invalid_data` code, or an exclusion reason. */
  reason: string | null;
  /** Catalog cost (net cents, four decimals). Cost-bearing. */
  costNetCents: string | null;
  currentSellNetCents: string | null;
  newSellNetCents: string | null;
  changeNetCents: string | null;
  margin: { percent: string; source: 'part_override' | 'category_rule'; categoryId: string | null; ruleId: string | null } | null;
}

export interface RecalculationSummary {
  change: number;
  unchanged: number;
  noRule: number;
  invalidData: number;
  excluded: number;
}

export interface RecalculationPreview {
  categoryId: string;
  requestedCount: number;
  hasMore: boolean;
  nextAfterPartId: string | null;
  summary: RecalculationSummary;
  rows: RecalculationRow[];
  previewToken: string;
}

export interface RecalculationApplyResult {
  categoryId: string;
  appliedCount: number;
  summary: RecalculationSummary;
  rows: Omit<RecalculationRow, 'costNetCents'>[];
  history: {
    partId: string;
    changeSeq: number | string;
    oldNetCents: string | null;
    newNetCents: string;
    source: string;
    changedBy: string | null;
  }[];
}

// Request bodies (what the staff-api strict schemas accept)

export interface PutCategoryRuleBody {
  marginPercent: string;
  isActive?: boolean;
}

export type CreateCustomerPriceBody = (
  | { companyId: string; personId?: undefined }
  | { personId: string; companyId?: undefined }
) &
  (
    | { partId: string; categoryId?: undefined }
    | { categoryId: string; partId?: undefined }
  ) & {
    priceType: RulePriceType;
    value: string;
    validFrom?: string | null;
    validTo?: string | null;
    isActive?: boolean;
    note?: string | null;
  };

export interface UpdateCustomerPriceBody {
  priceType?: RulePriceType;
  value?: string;
  validFrom?: string | null;
  validTo?: string | null;
  isActive?: boolean;
  note?: string | null;
}

export interface CreateCampaignBody {
  name: string;
  startsOn: string;
  endsOn: string;
  isActive?: boolean;
}

export interface UpdateCampaignBody {
  name?: string;
  startsOn?: string;
  endsOn?: string;
  isActive?: boolean;
}

export type CampaignItemBody = (
  | { partId: string; categoryId?: undefined }
  | { categoryId: string; partId?: undefined }
) & { priceType: RulePriceType; value: string };

export interface UpdateCampaignItemBody {
  priceType?: RulePriceType;
  value?: string;
}

export interface UpdatePartPricingBody {
  priceMode?: PartPriceMode;
  /** `null` clears the override. */
  marginOverridePercent?: string | null;
}

export interface RecalculationPreviewBody {
  categoryId: string;
  /** Cursor of the server-selected page (the `nextAfterPartId` of the page before). */
  afterPartId?: string;
}

export interface RecalculationApplyBody {
  categoryId: string;
  /** Exactly the parts of the displayed preview (<= 200). */
  partIds: string[];
  previewToken: string;
}

// ---------------------------------------------------------------------------
// Scope, permissions and keys
// ---------------------------------------------------------------------------

export const PRICING_PAGE_SIZE = 50;

const RULES_ENDPOINT = '/inventory/part-pricing';
const RESOLVE_ENDPOINT = '/inventory/part-prices/resolve';

export type { PricingScope };
export { PricingAccessError };

/** The signed-in tenant, user and session family; null while nobody is signed in (every query is then disabled). */
export function usePricingScope(): PricingScope | null {
  const { owner } = usePricingAccess();
  return useMemo(() => (owner ? scopeOfOwner(owner) : null), [owner]);
}

export interface PricingPermissions {
  /** Browse rules, campaigns and per-part prices (`inventory.read`). */
  canRead: boolean;
  /** Create, edit and delete (`inventory.pricing.manage`). */
  canManage: boolean;
  /** Customers' names in the rule tables (`customers.read`). */
  canReadCustomers: boolean;
  /** Cost-bearing category preview and apply: manage AND `reports.view_financial`. */
  canRecalculate: boolean;
}

/** The live permissions of the signed-in, unlocked session. Nothing is granted while signed out or locked. */
export function usePricingPermissions(): PricingPermissions {
  const { canRead, canManage, canReadCustomers, canRecalculate } = usePricingAccess();
  return { canRead, canManage, canReadCustomers, canRecalculate };
}

const familySegment = (scope: PricingScope): string[] => (scope.sessionFamilyId ? [scope.sessionFamilyId] : []);
const ownerSegments = (scope: PricingScope): string[] => [scope.tenantId, scope.userId, ...familySegment(scope)];

export const partPricingKeys = {
  all: (scope: PricingScope) => ['inventory', 'part-pricing', ...ownerSegments(scope)] as const,
  categoryRules: (scope: PricingScope) => [...partPricingKeys.all(scope), 'category-rules'] as const,
  customerPrices: (scope: PricingScope, filters: CustomerPriceFilters) =>
    [...partPricingKeys.all(scope), 'customer-prices', filters] as const,
  campaigns: (scope: PricingScope, filters: CampaignFilters) =>
    [...partPricingKeys.all(scope), 'campaigns', filters] as const,
  campaign: (scope: PricingScope, id: string) => [...partPricingKeys.all(scope), 'campaign', id] as const,
  resolve: (scope: PricingScope, body: ResolvePartPricesBody) => [...partPricingKeys.all(scope), 'resolve', body] as const,
  partMode: (scope: PricingScope, partId: string) => [...partPricingKeys.all(scope), 'part-mode', partId] as const,
  /** Not under `all`: invalidating the pricing data must not reset it. */
  previewEpoch: (scope: PricingScope) => ['inventory', 'part-pricing-epoch', ...ownerSegments(scope)] as const,
  /**
   * The category, part, company and person lookups of the pickers. They are this module's own scoped queries: the shared
   * inventory/company/person hooks key their caches by filter only, so a name cached for another owner could be shown here.
   * Not under `all`: a pricing change does not refresh a part's name.
   */
  reference: (scope: PricingScope) => ['inventory', 'part-pricing-ref', ...ownerSegments(scope)] as const,
};

const OWNED_ROOTS = ['part-pricing', 'part-pricing-epoch', 'part-pricing-ref'] as const;

/**
 * Drops every pricing query of another owner as soon as the owner changes, so a cached answer of a previous session, tenant or user
 * is not merely unreachable (the keys already differ) but gone, together with the names it held.
 */
export function usePricingCacheGuard(): void {
  const client = useQueryClient();
  const scope = usePricingScope();
  const { canReadCustomers } = usePricingAccess();
  const id = scope ? JSON.stringify(ownerSegments(scope)) : 'none';
  useEffect(() => {
    const own = scope ? ownerSegments(scope) : null;
    for (const root of OWNED_ROOTS) {
      client.removeQueries({
        queryKey: ['inventory', root],
        predicate: (query) => {
          if (own === null) return true;
          const key = query.queryKey;
          return !own.every((segment, index) => key[2 + index] === segment);
        },
      });
    }
    // `scope` is described by `id`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, id]);

  // Losing `customers.read` takes the names it showed out of the cache, not only off the screen.
  useEffect(() => {
    if (!scope || canReadCustomers) return;
    for (const kind of ['companies', 'persons', 'company', 'person']) {
      client.removeQueries({ queryKey: [...partPricingKeys.reference(scope), kind] });
    }
    // `scope` is described by `id`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, id, canReadCustomers]);
}

/**
 * Counts every pricing change this client made. A displayed recalculation preview remembers the
 * count it was taken at; a higher one means a rule, mode or price changed since, so the preview
 * is outdated and must be taken again.
 */
export function usePricingEpoch(): number {
  const scope = usePricingScope();
  const client = useQueryClient();
  const key = scope ? partPricingKeys.previewEpoch(scope) : (['inventory', 'part-pricing-epoch'] as const);
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => client.getQueryData<number>(key) ?? 0,
    initialData: 0,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: scope !== null,
  });
  return data;
}

function afterPricingChange(client: QueryClient, owner: PricingOwner, options: { catalog: boolean }): void {
  const scope = scopeOfOwner(owner);
  const epochKey = partPricingKeys.previewEpoch(scope);
  client.setQueryData<number>(epochKey, (current) => (current ?? 0) + 1);
  void client.invalidateQueries({ queryKey: partPricingKeys.all(scope) });
  if (options.catalog) {
    // The catalog price or mode of a part changed: part lists and details show it.
    void client.invalidateQueries({ queryKey: queryKeys.inventory.parts.all });
    void client.invalidateQueries({ queryKey: [...partPricingKeys.reference(scope), 'part'] });
    void client.invalidateQueries({ queryKey: [...partPricingKeys.reference(scope), 'parts'] });
  }
}

// ---------------------------------------------------------------------------
// Fenced requests
// ---------------------------------------------------------------------------

async function settle<T>(call: () => Promise<T>): Promise<PricingOutcome<T>> {
  try {
    return { ok: true, value: await call() };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * One request of the session that offered it. The owner and the permissions are checked again from the live store just before the
 * request leaves (a refusal before anything is sent), and again when the answer arrives: an answer that outlived its session,
 * tenant, user, generation or a needed permission is carried in a `LateResultError`, never returned, never thrown as its own error.
 */
export async function runOwned<T>(access: Pick<PricingAccess, 'owner' | 'epoch'>, permissions: readonly string[], call: () => Promise<T>): Promise<T> {
  const fence = takeFence(access, permissions);
  const outcome = await settle(call);
  if (!fenceHolds(fence)) throw new LateResultError(outcome);
  if (!outcome.ok) throw outcome.error;
  return outcome.value;
}

interface FencedMutationConfig<TVars, TData> {
  permissions: readonly string[];
  run: (vars: TVars) => Promise<TData>;
  /** Runs only while the session that started the request is still the live one. */
  onSuccess?: (data: TData, vars: TVars, owner: PricingOwner) => void;
  onError?: (error: unknown, vars: TVars, owner: PricingOwner) => void;
}

export interface FencedMutation<TVars, TData> {
  /** Rejects with `PricingAccessError` / `OwnerChangedError` before anything is sent, and with `LateResultError` for a late answer. */
  mutateAsync: (vars: TVars) => Promise<TData>;
  isPending: boolean;
  error: unknown;
}

/**
 * A write bound to the access of the render that offered it. A click handler of an earlier render keeps that render's owner, so it
 * cannot send under another session's credentials; the cache effects and the screen's callbacks only run for the live owner.
 */
function useFencedMutation<TVars, TData>(config: FencedMutationConfig<TVars, TData>): FencedMutation<TVars, TData> {
  const access = usePricingAccess();
  const [pending, setPending] = useState(0);
  const [failure, setFailure] = useState<{ owner: string; error: unknown } | null>(null);
  const ownerKey = access.owner ? ownerId(access.owner) : 'none';

  const mutateAsync = async (vars: TVars): Promise<TData> => {
    const fence = takeFence(access, config.permissions);
    setPending((count) => count + 1);
    setFailure(null);
    const outcome = await settle(() => config.run(vars));
    setPending((count) => Math.max(count - 1, 0));
    if (!fenceHolds(fence)) throw new LateResultError(outcome);
    if (!outcome.ok) {
      setFailure({ owner: ownerKey, error: outcome.error });
      config.onError?.(outcome.error, vars, fence.owner);
      throw outcome.error;
    }
    config.onSuccess?.(outcome.value, vars, fence.owner);
    return outcome.value;
  };

  return { mutateAsync, isPending: pending > 0, error: failure && failure.owner === ownerKey ? failure.error : null };
}

// ---------------------------------------------------------------------------
// Uncertain outcomes of idempotent requests
// ---------------------------------------------------------------------------

export interface KeyedRequest<TBody> {
  /** The `Idempotency-Key`; stays with the payload for every retry. */
  key: string;
  body: TBody;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

interface ErrorLike {
  status?: unknown;
  data?: unknown;
}

/**
 * True only when the server decided (or the client refused before sending): a 4xx that is not an auth/session guard, a timeout, or the
 * idempotency plugin's own "still running / outcome unknown". Everything else (a lost connection, a
 * 5xx, a locked session, an expired token) leaves the original write's fate open and keeps the key.
 */
export function isDefiniteRefusal(error: unknown): boolean {
  // Refused on the client before any request left: nothing can be pending.
  if (isLocalRefusal(error)) return true;
  const { status, data } = (error ?? {}) as ErrorLike;
  if (typeof status !== 'number' || status < 400 || status >= 500) return false;
  if ([401, 403, 408, 423, 425].includes(status)) return false;
  const body = (typeof data === 'object' && data !== null ? data : {}) as { code?: unknown; error?: unknown; outcome?: unknown };
  const unresolved = ['IDEMPOTENCY_IN_PROGRESS', 'IDEMPOTENCY_OUTCOME_UNKNOWN'];
  if (unresolved.includes(String(body.code)) || unresolved.includes(String(body.error))) return false;
  if (['unknown', 'pending', 'in_progress'].includes(String(body.outcome))) return false;
  return true;
}

export type KeyedSubmitResult<TResult> =
  | { status: 'success'; result: TResult }
  /** `attempts` is how many times this same request had been sent when the server refused it (> 1: a retry). */
  | { status: 'refused'; error: unknown; attempts: number }
  | { status: 'uncertain'; error: unknown }
  | { status: 'busy' }
  | { status: 'blocked' }
  /**
   * Nothing may be reported to this screen: the session, tenant, user, generation or a needed permission changed, either before the
   * request was sent (then nothing was) or while it was on its way (its answer was kept for its owner only). `error` is set when
   * the client refused locally. A request whose outcome is unknown stays unknown.
   */
  | { status: 'canceled'; error?: unknown };

export interface KeyedSubmitOptions<TBody = unknown> {
  /** Persist the request under the owner's journal so a remount or reload finds it again. Omitted: held in memory for this hook only. */
  journal?: {
    kind: JournalKind;
    target: string | null;
    /** The target taken from the payload (a category id); overrides `target`. */
    targetOf?: (body: TBody) => string | null;
  };
  /** Permissions that must still be held at the moment of every dispatch (re-read from the store each time). */
  permissions?: readonly string[];
}

export interface KeyedSubmit<TBody, TResult> {
  /** Starts a new operation with a new key. `blocked` while an uncertain one is still held. */
  submit: (body: TBody) => Promise<KeyedSubmitResult<TResult>>;
  /** Sends the held request again: the same payload and the same key, only for its own session. */
  retry: () => Promise<KeyedSubmitResult<TResult>>;
  /** Gives the held request up. Only an explicit, confirmed user action may call this. */
  discard: () => void;
  /** The request held after an uncertain outcome (also one found again after a reload), if any. */
  held: KeyedRequest<TBody> | null;
  /** How many times the held request was sent. */
  attempts: number;
  /** A request of this slot is on its way, also one started by a screen that has since unmounted. */
  isPending: boolean;
  /** The last answer of this screen's own attempt; `null` for a request found again after a reload. */
  error: unknown;
  /** `unavailable`: the browser keeps nothing, a reload cannot recover the request. `corrupt`: a record could not be read, nothing new starts. */
  journalStatus: JournalStatus;
  /** The user knowingly discards a damaged journal record (it was never used). */
  discardDamaged: () => void;
}

let ephemeralCounter = 0;

/** Too many unresolved requests are kept: nothing new starts until one is resolved or discarded. */
export class JournalFullError extends Error {
  constructor() {
    super('Too many unresolved pricing requests');
    this.name = 'JournalFullError';
  }
}

/**
 * Owns one idempotent operation of a form: its payload and key. After an uncertain outcome the very same request is held, `submit`
 * refuses to start another, and `retry` resends it unchanged. A determinate refusal releases the key (the server stored that 4xx
 * under it, so reusing it would replay the refusal forever) and the next `submit` is a new operation on purpose.
 *
 * The request belongs to the session that started it. Another tenant, user, session family or generation neither sees nor sends it,
 * a late answer of an old session is not reported to the current one, and a refusal of the CLIENT before a retry (permission lost,
 * session changed) never resolves or deletes the unknown outcome of the earlier attempt.
 */
export function useKeyedSubmit<TBody, TResult>(
  run: (request: KeyedRequest<TBody>) => Promise<TResult>,
  options: KeyedSubmitOptions<TBody> = {},
): KeyedSubmit<TBody, TResult> {
  const access = usePricingAccess();
  const view = useJournalView(access.owner);
  const ephemeralId = useRef<number>(0);
  if (ephemeralId.current === 0) ephemeralId.current = ++ephemeralCounter;
  const journal = options.journal;
  const slot = journal ? slotOf(journal.kind, journal.target) : `ephemeral:${ephemeralId.current}`;
  const permissions = options.permissions ?? [];
  const ownerKey = access.owner ? ownerId(access.owner) : 'none';
  const [failure, setFailure] = useState<{ owner: string; error: unknown } | null>(null);
  const mounted = useRef(true);
  const latest = useRef({ owner: access.owner, slot, ephemeral: journal === undefined });
  latest.current = { owner: access.owner, slot, ephemeral: journal === undefined };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // A request held in memory for this hook alone has nobody to resolve it once the hook is gone.
      const { owner, slot: held, ephemeral } = latest.current;
      if (!ephemeral || owner === null) return;
      const current = getJournalView(owner);
      const entry = current.entries.find((candidate) => candidate.slot === held);
      if (entry && !current.inFlight.has(held)) removeEntry(owner, held, entry.request.key);
    };
  }, []);

  const entry = view.entries.find((candidate) => candidate.slot === slot);
  const inFlight = view.inFlight.has(slot);
  const held = entry && !(inFlight && entry.attempts <= 1) ? (entry.request as KeyedRequest<TBody>) : null;

  const report = (owner: PricingOwner, error: unknown) => {
    if (mounted.current) setFailure({ owner: ownerId(owner), error });
  };

  const send = async (stored: JournalEntry, fence: PricingFence, fresh: boolean): Promise<KeyedSubmitResult<TResult>> => {
    const { owner } = stored;
    const { key } = stored.request;
    if (!beginFlight(owner, slot, key)) return { status: 'busy' };
    recordAttempt(owner, slot, key, 1);
    const attempts = stored.attempts + 1;
    setFailure(null);
    const raw = await settle(() => run(stored.request as KeyedRequest<TBody>));
    // A late answer carries its own outcome: that is what the server decided for THIS request.
    const late = !raw.ok && raw.error instanceof LateResultError;
    const outcome: PricingOutcome<TResult> = !raw.ok && raw.error instanceof LateResultError ? (raw.error.outcome as PricingOutcome<TResult>) : raw;
    const current = !late && fenceHolds(fence);
    const sameOwner = fenceHolds({ ...fence, permissions: [] });

    let result: KeyedSubmitResult<TResult>;
    if (outcome.ok) {
      removeEntry(owner, slot, key);
      result = current ? { status: 'success', result: outcome.value } : { status: 'canceled' };
    } else if (isLocalRefusal(outcome.error)) {
      // Nothing was sent. A fresh request leaves nothing pending; a retry leaves the earlier unknown outcome exactly as it was.
      recordAttempt(owner, slot, key, -1);
      if (fresh) removeEntry(owner, slot, key);
      result = fresh && sameOwner ? { status: 'refused', error: outcome.error, attempts } : { status: 'canceled', error: outcome.error };
    } else if (isDefiniteRefusal(outcome.error)) {
      removeEntry(owner, slot, key);
      result = current ? { status: 'refused', error: outcome.error, attempts } : { status: 'canceled' };
    } else {
      // Unknown: the entry stays, for its owner.
      result = current ? { status: 'uncertain', error: outcome.error } : { status: 'canceled' };
    }
    endFlight(owner, slot, key);

    if (result.status === 'refused' || result.status === 'uncertain') report(owner, result.error);
    else if (result.status === 'canceled' && sameOwner && result.error !== undefined) report(owner, result.error);
    return result;
  };

  const localRefusal = (error: unknown): KeyedSubmitResult<TResult> =>
    error instanceof PricingAccessError ? { status: 'refused', error, attempts: 1 } : { status: 'canceled', error };

  const submit = async (body: TBody): Promise<KeyedSubmitResult<TResult>> => {
    let fence: PricingFence;
    try {
      fence = takeFence(access, permissions);
    } catch (error) {
      return localRefusal(error);
    }
    const current = getJournalView(fence.owner);
    if (current.status === 'corrupt') return { status: 'blocked' };
    if (current.inFlight.has(slot)) return { status: 'busy' };
    if (current.entries.some((candidate) => candidate.slot === slot)) return { status: 'blocked' };

    const request: KeyedRequest<TBody> = { key: newIdempotencyKey(), body };
    const stored = putEntry(fence.owner, {
      kind: journal ? journal.kind : 'ephemeral',
      target: journal ? (journal.targetOf ? journal.targetOf(body) : journal.target) : null,
      request,
      epoch: fence.epoch,
      persist: journal !== undefined,
      slot,
    });
    if (stored === 'conflict' || stored === 'corrupt') return { status: 'blocked' };
    if (stored === 'full') return { status: 'refused', error: new JournalFullError(), attempts: 1 };
    const entryNow = getJournalView(fence.owner).entries.find((candidate) => candidate.slot === slot);
    if (!entryNow) return { status: 'blocked' };
    return send(entryNow, fence, true);
  };

  const retry = async (): Promise<KeyedSubmitResult<TResult>> => {
    let fence: PricingFence;
    try {
      fence = takeFence(access, permissions);
    } catch (error) {
      // The earlier outcome stays unknown and stays held.
      return { status: 'canceled', error };
    }
    const current = getJournalView(fence.owner);
    if (current.status === 'corrupt') return { status: 'blocked' };
    if (current.inFlight.has(slot)) return { status: 'busy' };
    const stored = current.entries.find((candidate) => candidate.slot === slot);
    if (!stored) return { status: 'blocked' };
    // Sent under another generation of this page: only that generation may resend it (a reload has no epoch and may).
    if (stored.epoch !== null && stored.epoch !== fence.epoch) return { status: 'canceled', error: new OwnerChangedError() };
    return send(stored, fence, false);
  };

  const discard = () => {
    let fence: PricingFence;
    try {
      fence = takeFence(access, []);
    } catch {
      return;
    }
    const current = getJournalView(fence.owner);
    if (current.inFlight.has(slot)) return;
    const stored = current.entries.find((candidate) => candidate.slot === slot);
    if (stored) removeEntry(fence.owner, slot, stored.request.key);
    setFailure(null);
  };

  const discardDamaged = () => {
    try {
      const fence = takeFence(access, []);
      dropCorruptJournal(fence.owner);
    } catch {
      // Not this owner's to discard.
    }
  };

  return {
    submit,
    retry,
    discard,
    held,
    attempts: entry?.attempts ?? 0,
    isPending: inFlight,
    error: failure && failure.owner === ownerKey ? failure.error : null,
    journalStatus: journal ? view.status : 'ok',
    discardDamaged,
  };
}

const keyHeader = (key: string) => ({ headers: { 'Idempotency-Key': key } });

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** The next offset of an offset-paginated response, or undefined at the end (never loops on an empty page). */
function nextOffset<T>(page: OffsetPage<T>): number | undefined {
  const { offset, hasMore } = page.pagination;
  return hasMore && page.data.length > 0 ? offset + page.data.length : undefined;
}

export function flattenPages<T>(data: InfiniteData<OffsetPage<T>, unknown> | undefined): T[] {
  return data?.pages.flatMap((page) => page.data) ?? [];
}

const READ_PERMISSIONS: readonly string[] = [PRICING_READ_PERMISSION];

export function useCategoryMarginRules(options: { enabled?: boolean } = {}) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useInfiniteQuery({
    queryKey: scope ? partPricingKeys.categoryRules(scope) : ['inventory', 'part-pricing', 'category-rules', 'signed-out'],
    queryFn: ({ pageParam }) =>
      runOwned(access, READ_PERMISSIONS, () =>
        api.get<OffsetPage<CategoryMarginRule>>(`${RULES_ENDPOINT}/category-rules`, {
          params: { limit: PRICING_PAGE_SIZE, offset: pageParam },
        }),
      ),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    enabled: scope !== null && access.canRead && (options.enabled ?? true),
    staleTime: 30_000,
  });
}

export interface CustomerPriceFilters {
  party?: { kind: PricingPartyKind; id: string } | undefined;
  target?: { kind: PricingTargetKind; id: string } | undefined;
  /** The rule's own switch (not "active today": that is the server's `activeToday` on each row). */
  isActive?: boolean | undefined;
}

export function useCustomerPrices(filters: CustomerPriceFilters, options: { enabled?: boolean } = {}) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useInfiniteQuery({
    queryKey: scope ? partPricingKeys.customerPrices(scope, filters) : ['inventory', 'part-pricing', 'customer-prices', 'signed-out'],
    queryFn: ({ pageParam }) =>
      runOwned(access, READ_PERMISSIONS, () =>
        api.get<OffsetPage<CustomerPriceRule>>(`${RULES_ENDPOINT}/customer-prices`, {
          params: {
            limit: PRICING_PAGE_SIZE,
            offset: pageParam,
            companyId: filters.party?.kind === 'company' ? filters.party.id : undefined,
            personId: filters.party?.kind === 'person' ? filters.party.id : undefined,
            partId: filters.target?.kind === 'part' ? filters.target.id : undefined,
            categoryId: filters.target?.kind === 'category' ? filters.target.id : undefined,
            isActive: filters.isActive,
          },
        }),
      ),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    enabled: scope !== null && access.canRead && (options.enabled ?? true),
    staleTime: 30_000,
  });
}

export interface CampaignFilters {
  isActive?: boolean | undefined;
}

export function useCampaigns(filters: CampaignFilters, options: { enabled?: boolean } = {}) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useInfiniteQuery({
    queryKey: scope ? partPricingKeys.campaigns(scope, filters) : ['inventory', 'part-pricing', 'campaigns', 'signed-out'],
    queryFn: ({ pageParam }) =>
      runOwned(access, READ_PERMISSIONS, () =>
        api.get<OffsetPage<Campaign>>(`${RULES_ENDPOINT}/campaigns`, {
          params: { limit: PRICING_PAGE_SIZE, offset: pageParam, isActive: filters.isActive },
        }),
      ),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    enabled: scope !== null && access.canRead && (options.enabled ?? true),
    staleTime: 30_000,
  });
}

/** One campaign with its items, page by page (`items` is the offset page of the current request). */
export function useCampaign(id: string | null) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useInfiniteQuery({
    queryKey: scope && id ? partPricingKeys.campaign(scope, id) : ['inventory', 'part-pricing', 'campaign', 'none'],
    queryFn: ({ pageParam }) =>
      runOwned(access, READ_PERMISSIONS, () =>
        api.get<CampaignDetailPage>(`${RULES_ENDPOINT}/campaigns/${id ?? ''}`, {
          params: { limit: PRICING_PAGE_SIZE, offset: pageParam },
        }),
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => nextOffset(last.items),
    enabled: scope !== null && access.canRead && id !== null,
    staleTime: 30_000,
  });
}

/** Plain request of the resolve endpoint (for callers that are not a hook, e.g. an add-part flow). */
export function resolvePartPrices(body: ResolvePartPricesBody): Promise<ResolvePartPricesResponse> {
  return api.post<ResolvePartPricesResponse>(RESOLVE_ENDPOINT, body);
}

/**
 * The server-resolved prices of parts, optionally for a booking or a party. The tenant-local date is the
 * server's; the response says which (`asOf`).
 */
export function usePartPriceResolution(body: ResolvePartPricesBody | null, options: { enabled?: boolean } = {}) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useQuery({
    queryKey: scope && body ? partPricingKeys.resolve(scope, body) : ['inventory', 'part-pricing', 'resolve', 'none'],
    queryFn: () => runOwned(access, READ_PERMISSIONS, () => resolvePartPrices(body as ResolvePartPricesBody)),
    enabled: scope !== null && access.canRead && body !== null && body.partIds.length > 0 && (options.enabled ?? true),
    staleTime: 15_000,
  });
}

/** The answer of the current-pricing read, checked: another shape or another part is an error, never a guessed state. */
function parseCurrentPartPricing(partId: string, raw: unknown): CurrentPartPricing {
  const body = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const { id, priceMode, marginOverridePercent } = body;
  const overrideOk = marginOverridePercent === null || typeof marginOverridePercent === 'string';
  if (id !== partId || typeof priceMode !== 'string' || !overrideOk || typeof body['sellPriceNetCents'] !== 'string') {
    throw new Error('Unexpected answer of the part pricing read');
  }
  return {
    id,
    // Anything but the two known modes is `unknown`: the screen must not present it as one of them.
    priceMode: priceMode === 'manual' || priceMode === 'category_margin' ? priceMode : 'unknown',
    marginOverridePercent: marginOverridePercent as string | null,
    sellPriceNetCents: body['sellPriceNetCents'],
    updatedAt: typeof body['updatedAt'] === 'string' ? body['updatedAt'] : '',
  };
}

/**
 * The CURRENT mode, margin override and status of one part, from the server (`GET /part-pricing/parts/:partId`, `inventory.read`).
 * Owned and fenced like every pricing read: the key carries the owner, the request is bound to the session and generation it began
 * under, an answer that outlived them never reaches the cache, and the cache of another owner is removed (never a fallback). There is
 * no placeholder: while the read is loading, failed or answered 404 there is no data, so nothing editable can be built on a guess.
 * A refetch (focus, invalidation after a save) replaces `data`; drafts live in the screen and are not touched by it.
 */
export function usePartPricing(partId: string | null) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useQuery({
    queryKey: scope && partId ? partPricingKeys.partMode(scope, partId) : ['inventory', 'part-pricing', 'part-mode', 'none'],
    queryFn: async (): Promise<CurrentPartPricing> => {
      const id = partId as string;
      const raw = await runOwned(access, READ_PERMISSIONS, () => api.get<unknown>(`${RULES_ENDPOINT}/parts/${encodeURIComponent(id)}`));
      return parseCurrentPartPricing(id, raw);
    },
    enabled: scope !== null && access.canRead && partId !== null,
    staleTime: 0,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

function useManage<TVars, TData>(
  run: (vars: TVars) => Promise<TData>,
  effects: { catalog?: boolean; onSuccessExtra?: (data: TData, vars: TVars, client: QueryClient, owner: PricingOwner) => void } = {},
): FencedMutation<TVars, TData> {
  const client = useQueryClient();
  return useFencedMutation<TVars, TData>({
    permissions: MANAGE_PERMISSIONS,
    run,
    onSuccess: (data, vars, owner) => {
      effects.onSuccessExtra?.(data, vars, client, owner);
      afterPricingChange(client, owner, { catalog: effects.catalog ?? false });
    },
  });
}

export function usePutCategoryRule() {
  return useManage(({ categoryId, body }: { categoryId: string; body: PutCategoryRuleBody }) =>
    api.put<CategoryMarginRule>(`${RULES_ENDPOINT}/category-rules/${categoryId}`, body),
  );
}

export function useDeleteCategoryRule() {
  return useManage((categoryId: string) => api.delete<unknown>(`${RULES_ENDPOINT}/category-rules/${categoryId}`));
}

export function useCreateCustomerPrice() {
  return useManage(({ key, body }: KeyedRequest<CreateCustomerPriceBody>) =>
    api.post<CustomerPriceSaved>(`${RULES_ENDPOINT}/customer-prices`, body, keyHeader(key)),
  );
}

export function useUpdateCustomerPrice() {
  return useManage(({ id, body }: { id: string; body: UpdateCustomerPriceBody }) =>
    api.patch<CustomerPriceSaved>(`${RULES_ENDPOINT}/customer-prices/${id}`, body),
  );
}

export function useDeleteCustomerPrice() {
  return useManage((id: string) => api.delete<unknown>(`${RULES_ENDPOINT}/customer-prices/${id}`));
}

export function useCreateCampaign() {
  return useManage(({ key, body }: KeyedRequest<CreateCampaignBody>) =>
    api.post<{ campaign: Campaign; items: CampaignItemSaved[] }>(`${RULES_ENDPOINT}/campaigns`, body, keyHeader(key)),
  );
}

export function useUpdateCampaign() {
  return useManage(({ id, body }: { id: string; body: UpdateCampaignBody }) => api.patch<Campaign>(`${RULES_ENDPOINT}/campaigns/${id}`, body));
}

export function useDeleteCampaign() {
  return useManage((id: string) => api.delete<unknown>(`${RULES_ENDPOINT}/campaigns/${id}`));
}

export function useAddCampaignItem(campaignId: string) {
  return useManage(({ key, body }: KeyedRequest<CampaignItemBody>) =>
    api.post<CampaignItemSaved>(`${RULES_ENDPOINT}/campaigns/${campaignId}/items`, body, keyHeader(key)),
  );
}

export function useUpdateCampaignItem(campaignId: string) {
  return useManage(({ itemId, body }: { itemId: string; body: UpdateCampaignItemBody }) =>
    api.patch<CampaignItemSaved>(`${RULES_ENDPOINT}/campaigns/${campaignId}/items/${itemId}`, body),
  );
}

export function useDeleteCampaignItem(campaignId: string) {
  return useManage((itemId: string) => api.delete<unknown>(`${RULES_ENDPOINT}/campaigns/${campaignId}/items/${itemId}`));
}

/** Mode and margin override of one part; never writes a price. */
export function useUpdatePartPricingMode() {
  return useManage(
    ({ partId, body }: { partId: string; body: UpdatePartPricingBody }) => api.patch<PartPricingModeResult>(`${RULES_ENDPOINT}/parts/${partId}`, body),
    {
      catalog: true,
      onSuccessExtra: (result, { partId }, client, owner) => {
        client.setQueryData(partPricingKeys.partMode(scopeOfOwner(owner), partId), result);
      },
    },
  );
}

/**
 * Cost-bearing preview of one page (<= 200 parts) of a category's `category_margin` parts. Refuses
 * before any request unless the user may manage pricing AND read financial data, now: a downgrade after the screen
 * rendered is read from the store at dispatch, and a preview that arrives after one is never delivered.
 */
export function usePreviewCategoryRecalculation() {
  return useFencedMutation<RecalculationPreviewBody, RecalculationPreview>({
    permissions: RECALCULATE_PERMISSIONS,
    run: (body) => api.post<RecalculationPreview>(`${RULES_ENDPOINT}/category-recalculation/preview`, body),
  });
}

/**
 * Applies exactly the previewed parts under the preview token. The key travels with the request;
 * the screen never retries with another token (a stale 409 needs a new preview and a new review).
 */
export function useApplyCategoryRecalculation() {
  const client = useQueryClient();
  return useFencedMutation<KeyedRequest<RecalculationApplyBody>, RecalculationApplyResult>({
    permissions: RECALCULATE_PERMISSIONS,
    run: ({ key, body }) => api.post<RecalculationApplyResult>(`${RULES_ENDPOINT}/category-recalculation/apply`, body, keyHeader(key)),
    onSuccess: (_result, _request, owner) => afterPricingChange(client, owner, { catalog: true }),
    // A stale refusal changes nothing server side, but the displayed numbers are wrong: refresh what depends on them.
    onError: (error, _request, owner) => {
      if ((error as ErrorLike | null)?.status === 409) {
        void client.invalidateQueries({ queryKey: partPricingKeys.all(scopeOfOwner(owner)) });
      }
    },
  });
}
